import React from "react";
import { useQuery } from "@tanstack/react-query";
import { StatusPill } from "components/admin";
import { CrudSection } from "components/admin/workspace-crud";
import {
  buildSchoolSportCreateValues,
  buildSchoolSportFields,
  buildSchoolSportUpdateValues,
} from "pages/app/workspace/sections/forms/school-admin-section-forms";
import { useScopedSchoolSelection } from "hooks/use-scoped-school-selection";
import { workspaceApi } from "pages/app/workspace/services";
import { hasPortalAction } from "utils/portal-access";
export const SchoolSportsSection: React.FC<{ embedded?: boolean; portalAccess: any }> = ({
  embedded = false,
  portalAccess,
}) => {
  const { schoolId } = useScopedSchoolSelection({
    portalAccess,
  });
  const sportsQuery = useQuery({
    queryKey: ["workspace-school-sports-reference"],
    queryFn: () => workspaceApi.sports.list(),
  });

  const schoolSportsQuery = useQuery({
    queryKey: ["workspace-school-sports", schoolId],
    queryFn: () => workspaceApi.schoolSports.list(schoolId),
    enabled: Boolean(schoolId),
  });

  const canManageSchoolSports = hasPortalAction(portalAccess, "school_sports.manage");
  const schoolSportFields = buildSchoolSportFields(sportsQuery.data || []);

  return (
    <CrudSection
      buildCreateValues={() => buildSchoolSportCreateValues(sportsQuery.data || [])}
      buildUpdateValues={buildSchoolSportUpdateValues}
      canCreate={canManageSchoolSports && Boolean(schoolId)}
      canEdit={canManageSchoolSports && Boolean(schoolId)}
      columns={[
        { label: "Sport", accessor: (row) => row.sport_name || "Unknown" },
        { label: "Status", accessor: (row) => <StatusPill value={row.status} /> },
        { label: "Notes", accessor: (row) => row.notes || "No notes" },
      ]}
      createLabel="Add school sport"
      data={schoolSportsQuery.data || []}
      description={
        canManageSchoolSports
          ? "Map platform sports into the selected school so downstream teams, players, and matches can scope correctly."
          : "Review the sports mapped into the selected school."
      }
      fields={schoolSportFields}
      invalidateKeys={[["workspace-school-sports", schoolId]]}
      isLoading={schoolSportsQuery.isLoading}
      onCreate={(payload) => workspaceApi.schoolSports.create(schoolId, payload)}
      onUpdate={(row, payload) => workspaceApi.schoolSports.update(schoolId, row.id, payload)}
      embedded={embedded}
      showTitle={!embedded}
      title="School Sports"
    />
  );
};
